import React, { useState, useCallback, useMemo } from 'react';
import { Calendar, Clock, Save, X, Eye, AlertCircle, Palette } from 'lucide-react';
import { BASE_URL } from "../../apiConfig";
import { useAuth } from "../../context/AuthProvider";
import { toast } from 'sonner';

export default function CreateAnnonceForm({ onCancel, onSuccess }) {
  const [formData, setFormData] = useState({
    annonce: "",
    date_start: "",
    date_end: "",
    color: "var(--color-primary-500)" // Default color
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { token } = useAuth();

  // Predefined color options
  const colorOptions = [
    { value: "var(--color-primary-500)", name: "Teal" },
    { value: "#f06548", name: "Coral" },
    { value: "#f7b84b", name: "Amber" },
    { value: "#3577f1", name: "Blue" },
    { value: "#925df0", name: "Purple" },
    { value: "#43cc78", name: "Green" }
  ];

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
    setSuccess(false);
    setErrorMessage("");
  }, [errors]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.annonce.trim()) {
      newErrors.annonce = "Le contenu de l'annonce est requis.";
    } else if (formData.annonce.trim().length < 10) {
      newErrors.annonce = "L'annonce doit contenir au moins 10 caractères.";
    }

    if (!formData.date_start) {
      newErrors.date_start = "La date de début est requise.";
    }

    if (!formData.date_end) {
      newErrors.date_end = "La date de fin est requise.";
    }

    if (formData.date_start && formData.date_end) {
      const startDate = new Date(formData.date_start);
      const endDate = new Date(formData.date_end);
      
      if (endDate <= startDate) {
        newErrors.date_end = "La date de fin doit être postérieure à la date de début.";
      }
    }

    if (!formData.color) {
      newErrors.color = "La couleur est requise.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = async (e) => {

    e.preventDefault();
    setErrorMessage("");

    if (!validateForm()) {
      setErrorMessage("Veuillez corriger les erreurs dans le formulaire.");
      toast.error("Veuillez corriger les erreurs dans le formulaire.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/api/annonces`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 422 && data.errors) {
          const validationErrors = {};
          Object.keys(data.errors).forEach(key => {
            validationErrors[key] = data.errors[key][0];
          });
          setErrors(validationErrors);
          setErrorMessage("Veuillez corriger les erreurs dans le formulaire.");
          toast.error("Veuillez corriger les erreurs dans le formulaire.");
        }
        return;
      }

      setSuccess(true);
      setFormData({ annonce: "", date_start: "", date_end: "", color: "var(--color-primary-500)" });
      setErrors({});
      toast.success("Annonce créée avec succès!");
      
      setTimeout(() => {
        setSuccess(false);
        onSuccess?.(data.annonce);
      }, 2000);

    } catch (error) {
      console.error("Error creating annonce:", error);
      setErrorMessage(error.message || "Une erreur inattendue est survenue. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  const previewData = useMemo(() => {
    const formatDate = (dateString) => {
      if (!dateString) return "";
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };

    const getDuration = () => {
      if (!formData.date_start || !formData.date_end) return "";
      const start = new Date(formData.date_start);
      const end = new Date(formData.date_end);
      const diffTime = end.getTime() - start.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return `${diffDays} jour${diffDays > 1 ? 's' : ''}`;
    };

    return {
      displayContent: formData.annonce || "Votre annonce apparaîtra ici...",
      displayStart: formatDate(formData.date_start) || "Date de début",
      displayEnd: formatDate(formData.date_end) || "Date de fin",
      duration: getDuration(),
      color: formData.color || "var(--color-primary-500)"
    };
  }, [formData]);

  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
      {success && (
        <div className="bg-[var(--color-primary-500)] text-white p-6 text-center">
          <div className="flex items-center justify-center mb-2">
            <Save className="h-8 w-8 mr-3" />
            <span className="text-lg font-semibold">Annonce créée avec succès !</span>
          </div>
          <p className="text-[var(--color-primary-50)] text-sm">Votre nouvelle annonce a été ajoutée au système.</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-8">
        {/* Annonce Content */}
        <div className="mb-8">
          <label htmlFor="annonce" className="block text-sm font-semibold text-gray-900 mb-3">
            Contenu de l'Annonce *
          </label>
          <textarea
            id="annonce"
            name="annonce"
            value={formData.annonce}
            onChange={handleInputChange}
            placeholder="Rédigez votre annonce ici..."
            rows={6}
            className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white resize-none ${
              errors.annonce
                ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
            }`}
          />
          <div className="flex justify-between items-center mt-2">
            {errors.annonce && <p className="text-red-600 text-xs flex items-center"><AlertCircle className="h-3 w-3 mr-1" />{errors.annonce}</p>}
            <p className="text-xs text-gray-500 ml-auto">
              {formData.annonce.length} caractères
            </p>
          </div>
        </div>

        {/* Date Range */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Start Date */}
          <div>
            <label htmlFor="date_start" className="block text-xs font-semibold text-gray-900 mb-3">
              Date de Début *
            </label>
            <div className="relative">
              <Calendar className="absolute cursor-pointer left-3 top-3.5 h-3 w-3 text-gray-400" />
              <input
                type="date"
                id="date_start"
                name="date_start"
                value={formData.date_start}
                onChange={handleInputChange}
                min={getTodayDate()}
                className={`w-full cursor-pointer py-3 pl-10 pr-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                  errors.date_start
                    ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                    : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                }`}
              />
            </div>
            {errors.date_start && <p className="mt-2 text-red-600 text-xs flex items-center"><AlertCircle className="h-3 w-3 mr-1" />{errors.date_start}</p>}
          </div>

          {/* End Date */}
          <div>
            <label htmlFor="date_end" className="block text-xs font-semibold text-gray-900 mb-3">
              Date de Fin *
            </label>
            <div className="relative">
              <Clock className="absolute cursor-pointer left-3 top-3.5 h-3 w-3 text-gray-400" />
              <input
                type="date"
                id="date_end"
                name="date_end"
                value={formData.date_end}
                onChange={handleInputChange}
                min={formData.date_start || getTodayDate()}
                className={`w-full cursor-pointer py-3 pl-10 pr-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                  errors.date_end
                    ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                    : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                }`}
              />
            </div>
            {errors.date_end && <p className="mt-2 text-red-600 text-xs flex items-center"><AlertCircle className="h-3 w-3 mr-1" />{errors.date_end}</p>}
          </div>
        </div>

        {/* Color Selection */}
        <div className="mb-8">
          <label className="block text-xs font-semibold text-gray-900 mb-3">
            Couleur de l'Annonce *
          </label>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {colorOptions.map((color) => (
              <div key={color.value} className="flex flex-col items-center">
                <input
                  type="radio"
                  id={`color-${color.value}`}
                  name="color"
                  value={color.value}
                  checked={formData.color === color.value}
                  onChange={handleInputChange}
                  className="sr-only"
                />
                <label
                  htmlFor={`color-${color.value}`}
                  className={`w-10 h-10 rounded-full cursor-pointer flex items-center justify-center border-2 transition-all ${
                    formData.color === color.value 
                      ? 'ring-2 ring-offset-2 ring-gray-400' 
                      : 'border-gray-200'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                >
                  {formData.color === color.value && (
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </label>
                <span className="text-xs text-gray-600 mt-1">{color.name}</span>
              </div>
            ))}
          </div>
          {errors.color && <p className="mt-2 text-red-600 text-xs flex items-center"><AlertCircle className="h-3 w-3 mr-1" />{errors.color}</p>}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 py-3 px-4 cursor-pointer text-xs border-2 border-gray-200 text-gray-700 font-semibold rounded-2xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="flex items-center justify-center">
              <X className="h-4 w-4 mr-2" />
              Annuler
            </span>
          </button>
          
          <button
            type="submit"
            disabled={isLoading}
            className={`flex-1 py-3 cursor-pointer px-4 text-xs font-semibold rounded-2xl transition-all duration-200 ${
              isLoading
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white shadow-lg hover:shadow-xl transform hover:scale-105"
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Création...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                <Save className="h-4 w-4 mr-2" />
                Créer l'Annonce
              </span>
            )}
          </button>
        </div>

        {/* Preview */}
        {(formData.annonce || formData.date_start || formData.date_end) && (
          <div className="mt-8 p-6 bg-gray-50 rounded-2xl">
            <div className="flex items-center gap-2 mb-4">
              <Eye className="h-5 w-5 text-[var(--color-primary-500)]" />
              <h3 className="font-semibold text-gray-900">Aperçu de l'Annonce</h3>
            </div>
            
            <div 
              className="rounded-2xl p-6 border border-gray-200 shadow-sm"
              style={{ 
                backgroundColor: `${previewData.color}15`, // Adding opacity to the color
                borderLeft: `4px solid ${previewData.color}`
              }}
            >
              <div className="mb-4">
                <p className="text-sm text-gray-800 leading-relaxed">
                  {previewData.displayContent}
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-100">
                <div className="flex items-center text-xs text-gray-600">
                  <Calendar className="h-3 w-3 mr-2" style={{ color: previewData.color }} />
                  <span>Début: {previewData.displayStart}</span>
                </div>
                <div className="flex items-center text-xs text-gray-600">
                  <Clock className="h-3 w-3 mr-2" style={{ color: previewData.color }} />
                  <span>Fin: {previewData.displayEnd}</span>
                </div>
                {previewData.duration && (
                  <div className="text-xs font-medium" style={{ color: previewData.color }}>
                    Durée: {previewData.duration}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}